import { Component, OnInit, inject, DestroyRef, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Validators, FormControl } from '@angular/forms';
import { ErrorMatcher } from 'src/app/shared/error.matcher';
import { AuthService } from 'src/app/shared/auth.service';
import { LoginActionStatusService } from 'src/app/shared/actionStatus.service';
import { ActivityService } from '../../shared/activity.service';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-inner-log-in',
	templateUrl: './inner-log-in.component.html',
	styleUrls: ['./inner-log-in.component.scss'],
	standalone: false // Keeping your traditional architecture
})
export class InnerLogInComponent implements OnInit {

	// 1. Modern Dependency Injection
	private readonly router = inject(Router);
	public readonly storage = inject(AngularFireStorage);
	private readonly auth = inject(AuthService);
	private readonly actionStatus = inject(LoginActionStatusService);
	private readonly activityService = inject(ActivityService);
	private readonly destroyRef = inject(DestroyRef); // Native auto-cleanup for subscriptions

	// 2. Signals for UI State
	readonly show = signal(false);
	readonly loading = signal(false);
	readonly incorrect = signal(false);
	readonly isManualCreated = signal(false);
	readonly notifyMode = signal(1);

	userInfo: any = null;

	// 3. Modern Strictly Typed Form Control
	passwordFormControl = new FormControl<string>('', {
		validators: [Validators.required],
		nonNullable: true
	});

	matcher = new ErrorMatcher();

	ngOnInit() {
		// 4. takeUntilDestroyed() automatically unsubscribes when the component is destroyed!
		this.auth.onUserUpdate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (userData) => {
				if (userData) this.loadChildData(userData);
			},
			error: (err) => console.log('error', err)
		});

		this.auth.onPaymentUpdate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: (paymentData) => {
				if (paymentData) this.isManualCreated.set(!paymentData.fromStripe);
			},
			error: (err) => console.log('error', err)
		});

		this.passwordFormControl.setValue('');

		this.actionStatus.action.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
			this.notifyMode.set(value);
			this.show.set(true);
		});
	}

	loadChildData(userData: any) {
		this.passwordFormControl.setValue('');
		this.userInfo = userData;
	}

	previousState() {
		this.router.navigate(['/game']);
	}

	async loginViaGoogle() {
		this.loading.set(true);
		this.incorrect.set(false);
		const googleRes = await this.auth.googleSignin();

		if (googleRes.user) {
			const userStr = JSON.stringify(googleRes.user);
			const userObj = JSON.parse(userStr);
			localStorage.setItem('currentUser', userStr);

			// 5. Replaced deprecated toPromise() with firstValueFrom()
			const parentData: any = await firstValueFrom(
				this.activityService.getSelfData({ token: userObj.stsTokenManager.accessToken })
			);

			if (!parentData?.result) {
				const parts = googleRes.user.displayName?.split(" ") || ["User", ""];
				const affiliateId = await this.generateAffiliateId({ token: userObj.stsTokenManager.accessToken });

				const value = {
					firstName: parts[0],
					lastName: googleRes.user.displayName?.replace(parts[0], "").trim() || "",
					email: googleRes.user.email,
					country: "Undefined",
					childs: [],
					regDate: new Date().getTime(),
					affiliateInfo: { affiliateId: affiliateId || 0, clicks: 0, registrations: 0, freeMonthsEarned: 0 }
				};

				await firstValueFrom(
					this.activityService.createSelfData({ token: userObj.stsTokenManager.accessToken, parent: value })
				);

				await this.sleep(3000);
			}

			let adminData: any = await firstValueFrom(
				this.activityService.getSelfData({ collectionID: "adminUserData", token: userObj.stsTokenManager.accessToken })
			);

			if (adminData?.result) {
				adminData = adminData.result;
				adminData.id = googleRes.user.uid;

				if (adminData['role'] === "super_administrator") {
					localStorage.setItem('superAdmin_accessToken', userObj.stsTokenManager.accessToken);
					localStorage.setItem('superAdmin_expirationTime', userObj.stsTokenManager.expirationTime);
					localStorage.setItem('superAdmin_userData', JSON.stringify({ ...adminData, uid: adminData.id }));

					await firstValueFrom(this.activityService.setSuperAdminAT({ token: userObj.stsTokenManager.accessToken }));

					this.router.navigate(['/admin/super/dashboard']);
				} else {
					localStorage.setItem('quranera_admin_accessToken', userObj.stsTokenManager.accessToken);
					localStorage.setItem('quranera_admin_expirationTime', userObj.stsTokenManager.expirationTime);
					localStorage.setItem('quranera_admin_userData', JSON.stringify({ ...adminData, uid: adminData.id }));
					this.router.navigate(['/admin']);
				}
			} else {
				this.loading.set(false);
				this.router.navigate(['/cabinet']);
			}
		} else {
			localStorage.removeItem('currentUser');
			this.loading.set(false);
		}
	}

	async loginViaApple() {
		this.loading.set(true);
		this.incorrect.set(false);
		const appleRes = await this.auth.appleSignin();

		if (appleRes.user) {
			const userStr = JSON.stringify(appleRes.user);
			const userObj = JSON.parse(userStr);
			localStorage.setItem('currentUser', userStr);

			const parentData: any = await firstValueFrom(
				this.activityService.getSelfData({ token: userObj.stsTokenManager.accessToken })
			);

			if (!parentData?.result) {
				const parts = appleRes.user.displayName?.split(" ") || ["User", ""];
				const affiliateId = await this.generateAffiliateId({ token: userObj.stsTokenManager.accessToken });

				const value = {
					firstName: parts[0],
					lastName: appleRes.user.displayName?.replace(parts[0], "").trim() || "",
					email: appleRes.user.email,
					country: "Undefined",
					provider: "Apple.com",
					childs: [],
					regDate: new Date().getTime(),
					affiliateInfo: { affiliateId: affiliateId || 0, clicks: 0, registrations: 0, freeMonthsEarned: 0 }
				};

				await firstValueFrom(
					this.activityService.createSelfData({ token: userObj.stsTokenManager.accessToken, parent: value })
				);
				await this.sleep(3000);
			}

			let adminData: any = await firstValueFrom(
				this.activityService.getSelfData({ collectionID: "adminUserData", token: userObj.stsTokenManager.accessToken })
			);

			if (adminData?.result) {
				adminData = adminData.result;
				adminData.id = appleRes.user.uid;

				if (adminData['role'] === "super_administrator") {
					localStorage.setItem('superAdmin_accessToken', userObj.stsTokenManager.accessToken);
					localStorage.setItem('superAdmin_expirationTime', userObj.stsTokenManager.expirationTime);
					localStorage.setItem('superAdmin_userData', JSON.stringify({ ...adminData, uid: adminData.id }));

					await firstValueFrom(this.activityService.setSuperAdminAT({ token: userObj.stsTokenManager.accessToken }));

					this.router.navigate(['/admin/super/dashboard']);
				} else {
					localStorage.setItem('quranera_admin_accessToken', userObj.stsTokenManager.accessToken);
					localStorage.setItem('quranera_admin_expirationTime', userObj.stsTokenManager.expirationTime);
					localStorage.setItem('quranera_admin_userData', JSON.stringify({ ...adminData, uid: adminData.id }));
					this.router.navigate(['/admin']);
				}
			} else {
				this.loading.set(false);
				this.router.navigate(['/cabinet']);
			}
		} else {
			localStorage.removeItem('currentUser');
			this.loading.set(false);
		}
	}

	async generateAffiliateId(data: any): Promise<any> {
		const result: any = await firstValueFrom(this.activityService.generateAID(data));
		return result.ID;
	}

	appendScriptToBody(email: string) {
		if (email && !document.getElementById('profitwell-js')) { // Prevents duplicate scripts!
			const s = document.createElement("script");
			s.setAttribute('id', 'profitwell-js');
			s.setAttribute('data-pw-auth', 'ddef438d0364d959a508bc1cc2c4d5c1');
			s.setAttribute('type', 'text/javascript');
			s.innerHTML = `(function(i,s,o,g,r,a,m){i[o]=i[o]||function(){(i[o].q=i[o].q||[]).push(arguments)}; a=s.createElement(g);m=s.getElementsByTagName(g)[0];a.async=1;a.src=r+'?auth='+s.getElementById(o+'-js').getAttribute('data-pw-auth');m.parentNode.insertBefore(a,m);})(window,document,'profitwell','script','https://public.profitwell.com/js/profitwell.js');profitwell('start', { 'user_email': '${email}' });`;
			document.body.appendChild(s);
		}
	}

	async login() {
		this.loading.set(true);
		this.incorrect.set(false);

		const parentEmail = localStorage.getItem("parentEmail");

		try {
			// Using async/await for cleaner try/catch logic
			const result = await this.auth.login(parentEmail, this.passwordFormControl.value);
			this.loading.set(false);
			localStorage.setItem('currentUser', JSON.stringify(result.user));

			if (!this.isManualCreated()) {
				await firstValueFrom(this.auth.updateParentNotifyfData({ notifyMode: this.notifyMode() }));
			}

			this.router.navigate(['/admin/cabinet']);
		} catch (error) {
			this.incorrect.set(true);
			this.loading.set(false);
		}
	}

	close(event: any) {
		try {
			if (event.target?.className?.includes('alert-pop-up')) {
				this.show.set(false);
			}
		} catch (e) { }
	}

	sleep(ms: number) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}
}
